import { IonFab, IonFabButton, IonFabList } from "@ionic/react";
import color from "color";
import Globals, { TResponseValue } from "globals";
import L10n from "localization";
import { useCallback, useEffect, useRef, useState } from "react";
import { PiArrowsClockwise, PiList } from "react-icons/pi";
import Style from "style";

interface IFabPosition {
  x: number;
  y: number;
}

interface IDraggableFabProps {
  enabled: boolean;
  activeSideNum: number;
  handleFlip: () => void;
  handleResponse: (response: TResponseValue) => void;
  faded?: boolean;
  isFSRS?: boolean;
}

const STORAGE_KEY = "reviewFabPosition";

function saveFabPosition(position: IFabPosition) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
}

function loadFabPosition(): IFabPosition | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

function getDefaultFabPosition(): IFabPosition {
  return {
    x: window.innerWidth - 120,
    y: window.innerHeight - 120,
  };
}

function isPositionValid(position: IFabPosition): boolean {
  const fabSize = 64; // Size of the fab button
  return (
    position.x >= 0 &&
    position.y >= 0 &&
    position.x <= window.innerWidth - fabSize &&
    position.y <= window.innerHeight - fabSize
  );
}

function fabButtonStyle(baseColor: string) {
  return {
    "--background": color(baseColor).alpha(0.9).string(),
    "--background-hover": color(baseColor).alpha(0.15).string(),
    "--background-activated": color(baseColor).alpha(0.5).string(),
    "--border-color": color(baseColor).alpha(1.0).string(),
    "--border-width": "2px",
    "--border-style": "solid",
    "--border-radius": "32px",
    minWidth: 64,
    minHeight: 64,
    fontWeight: "bold",
    "--color": "white",
  };
}

export default function DraggableFab({
  enabled,
  activeSideNum,
  handleFlip,
  handleResponse,
  faded = false,
  isFSRS,
}: IDraggableFabProps): JSX.Element {
  // Fab positioning state
  const [fabPosition, setFabPosition] = useState<IFabPosition>(() => {
    const savedPosition = loadFabPosition();
    if (savedPosition && isPositionValid(savedPosition)) {
      return savedPosition;
    }
    return getDefaultFabPosition();
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [fabInternalFaded, setFabInternalFaded] = useState(false);
  const fabRef = useRef<HTMLIonFabElement>(null);

  // Update position if saved position is invalid
  useEffect(() => {
    const savedPosition = loadFabPosition();
    if (!savedPosition || !isPositionValid(savedPosition)) {
      const actualDefault = getDefaultFabPosition();
      if (fabPosition.x !== actualDefault.x || fabPosition.y !== actualDefault.y) {
        setFabPosition(actualDefault);
      }
    }
  }, [fabPosition.x, fabPosition.y]);

  // Save position to localStorage when it changes
  useEffect(() => {
    saveFabPosition(fabPosition);
  }, [fabPosition]);

  // Handle drag start
  const handleDragStart = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      setIsDragging(true);

      // Capture pointer events on this element
      e.currentTarget.setPointerCapture(e.pointerId);

      setDragStart({
        x: e.clientX - fabPosition.x,
        y: e.clientY - fabPosition.y,
      });

      // Handle fab internal fading when response buttons are shown
      if (activeSideNum === 1) {
        setFabInternalFaded(true);
      }
    },
    [fabPosition, activeSideNum],
  );

  // Handle drag move
  const handleDragMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging || !dragStart) return;

      e.preventDefault();

      const newX = Math.max(0, Math.min(window.innerWidth - 64, e.clientX - dragStart.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 64, e.clientY - dragStart.y));

      setFabPosition({ x: newX, y: newY });
    },
    [isDragging, dragStart],
  );

  // Handle drag end
  const handleDragEnd = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return;

      setIsDragging(false);
      setDragStart(null);

      // Release pointer capture
      e.currentTarget.releasePointerCapture(e.pointerId);

      // Handle fab internal fading end when response buttons are shown
      if (activeSideNum === 1) {
        setFabInternalFaded(false);
      }
    },
    [isDragging, activeSideNum],
  );

  // Determine if fab is on left half of screen to position fail button appropriately
  const isOnLeftHalf = fabPosition.x < window.innerWidth / 2;
  const failButtonSide = isOnLeftHalf ? "end" : "start";

  return (
    <IonFab
      ref={fabRef}
      style={{
        position: "fixed",
        left: `${fabPosition.x}px`,
        top: `${fabPosition.y}px`,
        zIndex: 1000,
        cursor: isDragging ? "grabbing" : "grab",
        opacity: faded || fabInternalFaded ? 0.05 : 1,
        transition: "opacity 0.2s ease",
      }}
      activated={activeSideNum === 1}
    >
      {activeSideNum === 0 ? (
        <IonFabButton
          disabled={!enabled}
          onClick={handleFlip}
          onPointerDown={handleDragStart}
          onPointerMove={handleDragMove}
          onPointerUp={handleDragEnd}
          style={{
            touchAction: "none",
            userSelect: "none",
          }}
        >
          <PiArrowsClockwise />
        </IonFabButton>
      ) : (
        <div
          onPointerDown={handleDragStart}
          onPointerMove={handleDragMove}
          onPointerUp={handleDragEnd}
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            backgroundColor: "transparent",
            border: "none",
            cursor: isDragging ? "grabbing" : "grab",
            touchAction: "none",
            userSelect: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 0.2,
          }}
        >
          <PiList />
        </div>
      )}
      <IonFabList side="top" activated={activeSideNum === 1}>
        <IonFabButton
          show
          activated={false} // HACK: necessary to prevent an X icon from appearing on the Hard button when using keyboard to flip.
          style={fabButtonStyle(Style.colors.hard)}
          onClick={() => handleResponse(Globals.RESPONSES.HARD)}
          title={L10n.localize((s) => s.response.hard)}
        >
          {L10n.localize((s) => s.response.hard)}
        </IonFabButton>
        <IonFabButton
          show
          activated={false} // HACK: necessary to prevent an X icon from appearing on the Hard button when using keyboard to flip.
          style={fabButtonStyle(Style.colors.good)}
          onClick={() => handleResponse(Globals.RESPONSES.GOOD)}
          title={L10n.localize((s) => s.response.good)}
        >
          {L10n.localize((s) => s.response.good)}
        </IonFabButton>
        <IonFabButton
          show
          activated={false} // HACK: necessary to prevent an X icon from appearing on the Hard button when using keyboard to flip.
          style={fabButtonStyle(Style.colors.easy)}
          onClick={() => handleResponse(Globals.RESPONSES.EASY)}
          title={L10n.localize((s) => s.response.easy)}
        >
          {L10n.localize((s) => s.response.easy)}
        </IonFabButton>
      </IonFabList>
      <IonFabList side={failButtonSide}>
        <IonFabButton
          show
          activated={false} // HACK: necessary to prevent an X icon from appearing on the Hard button when using keyboard to flip.
          style={fabButtonStyle(Style.colors.fail)}
          onClick={() => handleResponse(Globals.RESPONSES.FAIL)}
          title={
            isFSRS ? L10n.localize((s) => s.response.again) : L10n.localize((s) => s.response.fail)
          }
        >
          {isFSRS ? L10n.localize((s) => s.response.again) : L10n.localize((s) => s.response.fail)}
        </IonFabButton>
      </IonFabList>
    </IonFab>
  );
}
